import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import './YoSign.css';

const YoSign = () => {
  const [messages, setMessages] = useState([]);
  const [clickCount, setClickCount] = useState(0);
  const [nextId, setNextId] = useState(0);

  const handleClick = (event) => {
    const newClickCount = clickCount + 1;
    setClickCount(newClickCount);

    // Get the sign's position from the click event
    const signRect = event.currentTarget.getBoundingClientRect();
    const signCenterX = signRect.left + signRect.width / 2;
    const signCenterY = signRect.top + signRect.height / 2;

    // Generate random position mostly to the left and above the sign
    const maxRadius = 350; // Max distance from sign
    // Bias angle toward upper-left quadrant (between PI/2 and PI, with some variance)
    // PI/2 = up, PI = left, so we want mostly between those
    const baseAngle = Math.PI * 0.75; // Center at upper-left (135 degrees)
    const angleSpread = Math.PI * 0.4; // Allow 72 degrees of spread
    const angle = baseAngle + (Math.random() - 0.5) * angleSpread;
    const distance = 80 + Math.random() * (maxRadius - 80); // Min 80px away

    // Calculate offset from sign center
    const offsetX = Math.cos(angle) * distance;
    const offsetY = Math.sin(angle) * distance;

    // Calculate absolute position
    let absoluteX = signCenterX + offsetX;
    let absoluteY = signCenterY + offsetY;

    // Get viewport dimensions
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    // Popup dimensions (matches actual SVG size in CSS: 240px x 80px)
    const popupWidth = 240;
    const popupHeight = 80;
    const padding = 20;      // Padding from edges

    // Since we use transform: translate(-50%, -50%), the popup is centered on the position
    // So we need half the dimensions for bounds checking
    const halfWidth = popupWidth / 2;
    const halfHeight = popupHeight / 2;

    // Constrain X position to stay within viewport
    if (absoluteX - halfWidth < padding) {
      absoluteX = halfWidth + padding;
    } else if (absoluteX + halfWidth > viewportWidth - padding) {
      absoluteX = viewportWidth - halfWidth - padding;
    }

    // Constrain Y position to stay within viewport
    if (absoluteY - halfHeight < padding) {
      absoluteY = halfHeight + padding;
    } else if (absoluteY + halfHeight > viewportHeight - padding) {
      absoluteY = viewportHeight - halfHeight - padding;
    }

    const newMessage = {
      id: nextId,
      x: absoluteX,
      y: absoluteY,
      usePixels: true // Flag to indicate we're using pixel positioning
    };

    setMessages([...messages, newMessage]);
    setNextId(nextId + 1);

    // Remove this message after 3 seconds
    setTimeout(() => {
      setMessages(prevMessages => prevMessages.filter(msg => msg.id !== newMessage.id));
    }, 3000);
  };

  return (
    <>
      <div className="yo-sign-container">
        <motion.div
          className="yo-sign"
          onClick={handleClick}
          whileHover={{ scale: 1.08, rotate: 2 }}
          whileTap={{ scale: 0.95 }}
          transition={{ type: "spring", stiffness: 300, damping: 15 }}
        >
          <svg
            viewBox="0 0 180 120"
            className="yo-svg"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g className="sign-background">
              <rect
                x="10"
                y="15"
                width="160"
                height="90"
                rx="8"
                fill="#FFF8DC"
                stroke="#8B7355"
                strokeWidth="3"
                opacity="0.95"
              />
              <line x1="15" y1="25" x2="165" y2="25" stroke="#D4A574" strokeWidth="1" opacity="0.4"/>
              <line x1="15" y1="50" x2="165" y2="50" stroke="#D4A574" strokeWidth="1" opacity="0.4"/>
              <line x1="15" y1="75" x2="165" y2="75" stroke="#D4A574" strokeWidth="1" opacity="0.4"/>
              <line x1="15" y1="95" x2="165" y2="95" stroke="#D4A574" strokeWidth="1" opacity="0.4"/>
            </g>

            <g className="yo-text">
              <path
                d="M 40 35 L 55 65 M 70 35 L 55 65 L 55 85"
                stroke="#FF6B35"
                strokeWidth="8"
                strokeLinecap="round"
                strokeLinejoin="round"
                fill="none"
                className="letter-y"
              />

              <ellipse
                cx="120"
                cy="60"
                rx="25"
                ry="28"
                stroke="#FF6B35"
                strokeWidth="8"
                fill="none"
                className="letter-o"
              />

              <circle cx="25" cy="30" r="4" fill="#FFD700" className="deco-dot"/>
              <circle cx="155" cy="30" r="4" fill="#FFD700" className="deco-dot"/>
              <circle cx="25" cy="90" r="4" fill="#FFD700" className="deco-dot"/>
              <circle cx="155" cy="90" r="4" fill="#FFD700" className="deco-dot"/>
            </g>

            <rect
              x="12"
              y="106"
              width="156"
              height="4"
              rx="2"
              fill="#000000"
              opacity="0.1"
            />
          </svg>
        </motion.div>
      </div>

      <AnimatePresence>
        {messages.map((message) => (
          <motion.div
            key={message.id}
            className="henryomi-message"
            style={{
              position: 'fixed',
              left: `${message.x}px`,
              top: `${message.y}px`,
              transform: 'translate(-50%, -50%)',
              zIndex: 9999
            }}
            initial={{ opacity: 0, scale: 0.5 }}
            animate={{
              opacity: 1,
              scale: 1,
              rotate: [0, -2, 2, -2, 0]
            }}
            exit={{ opacity: 0, scale: 0.8, y: -20 }}
            transition={{
              duration: 0.5,
              rotate: {
                duration: 0.5,
                repeat: Infinity,
                repeatType: "reverse",
                ease: "easeInOut"
              }
            }}
          >
            <svg viewBox="0 0 240 80" className="henryomi-svg">
              <path
                d="M 20 10 Q 10 10, 10 20 L 10 50 Q 10 60, 20 60 L 110 60 L 120 70 L 125 60 L 220 60 Q 230 60, 230 50 L 230 20 Q 230 10, 220 10 Z"
                fill="#FFE4B5"
                stroke="#FF6B35"
                strokeWidth="3"
                filter="drop-shadow(2px 4px 6px rgba(0,0,0,0.2))"
              />

              <text
                x="120"
                y="45"
                textAnchor="middle"
                fill="#FF6B35"
                fontSize="32"
                fontFamily="'Nanum Pen Script', cursive"
                fontWeight="bold"
                className="henryomi-text"
              >
                henr
                <tspan fill="#FFD700" fontSize="38">YO</tspan>
                mi
              </text>

              <circle cx="30" cy="25" r="3" fill="#FFD700" className="sparkle" opacity="0.8">
                <animate attributeName="opacity" values="0.3;1;0.3" dur="1.5s" repeatCount="indefinite"/>
              </circle>
              <circle cx="210" cy="25" r="3" fill="#FFD700" className="sparkle" opacity="0.8">
                <animate attributeName="opacity" values="1;0.3;1" dur="1.5s" repeatCount="indefinite"/>
              </circle>
              <circle cx="120" cy="15" r="2.5" fill="#FFD700" className="sparkle" opacity="0.8">
                <animate attributeName="opacity" values="0.5;1;0.5" dur="1.2s" repeatCount="indefinite"/>
              </circle>
            </svg>
          </motion.div>
        ))}
      </AnimatePresence>
    </>
  );
};

export default YoSign;
